export const MAX_PII_CHARS = 200_000;

export type PiiType =
	| 'email'
	| 'phone'
	| 'ipv4'
	| 'url'
	| 'credit_card'
	| 'pan_in'
	| 'aadhaar'
	| 'account';

export type PiiMatch = {
	id: string;
	type: PiiType;
	value: string;
	start: number;
	end: number;
	confidence: 'high' | 'medium' | 'low';
	preview: string;
	enabled: boolean;
};

export type PiiResult = {
	ok: boolean;
	message: string;
	matches: PiiMatch[];
	redacted: string;
};

export type DetectorOptions = Record<PiiType, boolean>;

export const DEFAULT_DETECTORS: DetectorOptions = {
	email: true,
	phone: true,
	ipv4: true,
	url: true,
	credit_card: true,
	pan_in: true,
	aadhaar: false,
	account: false,
};

function maskPreview(value: string): string {
	if (value.length <= 4) {
		return '•'.repeat(value.length);
	}
	return `${value.slice(0, 2)}${'•'.repeat(Math.min(8, value.length - 4))}${value.slice(-2)}`;
}

export function luhnOk(num: string): boolean {
	const digits = num.replace(/\D/g, '');
	if (digits.length < 13 || digits.length > 19) {
		return false;
	}
	let sum = 0;
	let alt = false;
	for (let i = digits.length - 1; i >= 0; i -= 1) {
		let n = Number(digits[i]);
		if (alt) {
			n *= 2;
			if (n > 9) {
				n -= 9;
			}
		}
		sum += n;
		alt = !alt;
	}
	return sum % 10 === 0;
}

type RawHit = Omit<PiiMatch, 'id' | 'enabled' | 'preview'>;

function collect(text: string, opts: DetectorOptions): RawHit[] {
	const hits: RawHit[] = [];

	const push = (type: PiiType, value: string, start: number, end: number, confidence: RawHit['confidence']) => {
		hits.push({ type, value, start, end, confidence });
	};

	if (opts.email) {
		const re = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
		for (const m of text.matchAll(re)) {
			if (m.index !== undefined) {
				push('email', m[0], m.index, m.index + m[0].length, 'high');
			}
		}
	}
	if (opts.phone) {
		const re = /(?:\+\d{1,3}[\s-]*)?(?:\(?\d{2,4}\)?[\s-]*)?\d{3,5}[\s-]?\d{3,5}(?:[\s-]?\d{2,5})?/g;
		for (const m of text.matchAll(re)) {
			const digits = m[0].replace(/\D/g, '');
			if (m.index === undefined || digits.length < 10 || digits.length > 15) {
				continue;
			}
			push('phone', m[0], m.index, m.index + m[0].length, 'medium');
		}
	}
	if (opts.ipv4) {
		const re = /\b(?:(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\b/g;
		for (const m of text.matchAll(re)) {
			if (m.index !== undefined) {
				push('ipv4', m[0], m.index, m.index + m[0].length, 'high');
			}
		}
	}
	if (opts.url) {
		const re = /\bhttps?:\/\/[^\s<>"']+/gi;
		for (const m of text.matchAll(re)) {
			if (m.index !== undefined) {
				push('url', m[0], m.index, m.index + m[0].length, 'high');
			}
		}
	}
	if (opts.credit_card) {
		const re = /\b(?:\d[ -]*?){13,19}\b/g;
		for (const m of text.matchAll(re)) {
			if (m.index === undefined) {
				continue;
			}
			if (!luhnOk(m[0])) {
				continue;
			}
			push('credit_card', m[0], m.index, m.index + m[0].length, 'high');
		}
	}
	if (opts.pan_in) {
		const re = /\b[A-Z]{5}[0-9]{4}[A-Z]\b/g;
		for (const m of text.matchAll(re)) {
			if (m.index !== undefined) {
				push('pan_in', m[0], m.index, m.index + m[0].length, 'medium');
			}
		}
	}
	if (opts.aadhaar) {
		const re = /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g;
		for (const m of text.matchAll(re)) {
			if (m.index !== undefined) {
				push('aadhaar', m[0], m.index, m.index + m[0].length, 'low');
			}
		}
	}
	if (opts.account) {
		const re = /\b(?:acct|account|a\/c)[:\s#-]*([A-Z0-9]{6,18})\b/gi;
		for (const m of text.matchAll(re)) {
			if (m.index !== undefined) {
				push('account', m[0], m.index, m.index + m[0].length, 'low');
			}
		}
	}

	hits.sort((a, b) => a.start - b.start || b.end - a.start - (a.end - a.start));
	const filtered: RawHit[] = [];
	let cursor = -1;
	for (const hit of hits) {
		if (hit.start < cursor) {
			continue;
		}
		filtered.push(hit);
		cursor = hit.end;
	}
	return filtered;
}

export type RedactionMode = 'token' | 'mask' | 'irreversible';

export function detectPii(
	text: string,
	opts: DetectorOptions = DEFAULT_DETECTORS,
	mode: RedactionMode = 'token',
	enabledIds?: Set<string>
): PiiResult {
	if (!text) {
		return { ok: false, message: 'Paste text to scan for likely personal data.', matches: [], redacted: '' };
	}
	if (text.length > MAX_PII_CHARS) {
		return {
			ok: false,
			message: `Input exceeds the ${MAX_PII_CHARS.toLocaleString()} character limit.`,
			matches: [],
			redacted: '',
		};
	}

	const raw = collect(text, opts);
	const counters: Partial<Record<PiiType, number>> = {};
	const stableTokens = new Map<string, string>();
	const matches: PiiMatch[] = raw.map((hit, index) => {
		counters[hit.type] = (counters[hit.type] || 0) + 1;
		const id = `${hit.type}-${index}`;
		return {
			...hit,
			id,
			preview: maskPreview(hit.value),
			enabled: enabledIds ? enabledIds.has(id) : true,
		};
	});

	let redacted = '';
	let pos = 0;
	for (const match of matches) {
		redacted += text.slice(pos, match.start);
		if (match.enabled) {
			const key = `${match.type}:${match.value}`;
			let replacement: string;
			if (mode === 'mask') {
				replacement = maskPreview(match.value);
			} else if (mode === 'irreversible') {
				replacement = '[REDACTED]';
			} else if (stableTokens.has(key)) {
				replacement = stableTokens.get(key)!;
			} else {
				const n = counters[match.type] || 1;
				// recount order among enabled of same type for stable labeling
				replacement = `[${match.type.toUpperCase()}_${n}]`;
				stableTokens.set(key, replacement);
			}
			redacted += replacement;
		} else {
			redacted += match.value;
		}
		pos = match.end;
	}
	redacted += text.slice(pos);

	// Fix token numbering by type in occurrence order among enabled
	if (mode === 'token') {
		const typeCount: Partial<Record<PiiType, number>> = {};
		const map = new Map<string, string>();
		redacted = text;
		// rebuild properly
		redacted = '';
		pos = 0;
		for (const match of matches) {
			redacted += text.slice(pos, match.start);
			if (match.enabled) {
				const key = `${match.type}:${match.value}`;
				if (!map.has(key)) {
					typeCount[match.type] = (typeCount[match.type] || 0) + 1;
					map.set(key, `[${match.type.toUpperCase()}_${typeCount[match.type]}]`);
				}
				redacted += map.get(key)!;
			} else {
				redacted += match.value;
			}
			pos = match.end;
		}
		redacted += text.slice(pos);
	}

	return {
		ok: true,
		message:
			matches.length > 0
				? `Found ${matches.length} likely match(es). Regex match is not identity verification.`
				: 'No detector matches in the selected classes. Some personal data may be missed.',
		matches,
		redacted,
	};
}

export const SAMPLE_PII = [
	'Support ticket SAMPLE ONLY',
	'Customer: Ada Example <ada.example@example.invalid>',
	'Mobile: +91 98765 43210',
	'Server IP: 203.0.113.10',
	'Card candidate: 4111 1111 1111 1111',
	'PAN-like: ABCDE1234F',
	'Docs: https://example.invalid/case/123',
	'Ignore HTML: <script>alert(1)</script>',
].join('\n');
